/**
 * ActionTile — soft tinted icon button.
 *
 * Same visual family as the sidebar/nav `IconTile`, but toned down:
 * a light washed background with the tint used for the icon color
 * (not the fill). Designed for row-level actions in tables (Invoices,
 * Estimates, Recurring, Customers, Employees, Admin panel, etc.) so
 * every dense-action surface shares one look.
 *
 * Props
 * -----
 * icon      Lucide icon component (required)
 * tint      Hex color (from TINT palette or custom); default TINT.blue
 * onClick   Handler
 * title     Tooltip + aria-label
 * disabled  Grays + blocks click
 * testid    data-testid string
 * primary   Extends into a pill with the label text (used for "Pay" etc.)
 * label     Optional text (only rendered when `primary` is true)
 */
import { TINT } from './IconTile';

export function ActionTile({
  icon: Icon,
  tint = TINT.blue,
  onClick,
  title,
  disabled,
  testid,
  primary,
  label,
}) {
  const size = primary ? 34 : 32;
  const softBg = `${tint}1F`;   // hex alpha 0x1F ≈ 12%
  const softBgHover = `${tint}33`; // 20%
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      data-testid={testid}
      style={{
        all: 'unset',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: primary ? 'auto' : size,
        height: size,
        minWidth: size,
        padding: primary ? '0 12px 0 8px' : 0,
        gap: primary ? 5 : 0,
        borderRadius: primary ? 999 : 9,
        background: softBg,
        color: tint,
        transition: 'background 0.15s ease, transform 0.15s ease',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: 0.2,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = softBgHover;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = softBg;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Icon size={primary ? 15 : 16} strokeWidth={2.25} />
      {primary && label ? label : null}
    </button>
  );
}

export default ActionTile;
